import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, Trash2, FolderOpen, Image, Plus, X, Download, AlertCircle, StepBack } from 'lucide-react';
import { fetchServices, rawServicesData as fallbackData } from '../data/servicesData';
import LogoutButton from './LogoutButton';
import ENV from '../config/env';

const API_BASE = ENV.API_BASE;
const FILE_SERVER_URL = ENV.FILE_SERVER_URL;

// Service Form Modal Component
const ServiceFormModal = ({ isOpen, onClose, onSave, service, mode }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        icon: ''
    });
    const [saving, setSaving] = useState(false);

    // Reset form when modal opens/closes or service changes
    useEffect(() => {
        if (isOpen) {
            if (service && mode === 'edit') {
                setFormData({
                    title: service.title || '',
                    description: service.description || '',
                    icon: service.icon || ''
                });
            } else {
                // Reset to empty for new service
                setFormData({
                    title: '',
                    description: '',
                    icon: ''
                });
            }
        }
    }, [isOpen, service, mode]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            const url = mode === 'edit'
                ? `${API_BASE}/services/${service.id}`
                : `${API_BASE}/services`;

            const method = mode === 'edit' ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                const result = await response.json();
                onSave(result.service);
                onClose();
            } else {
                const error = await response.json();
                alert(`Error: ${error.error || 'Error al guardar el servicio'}`);
            }
        } catch (error) {
            console.error('Error saving service:', error);
            alert('Error al guardar el servicio');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-gray-800 rounded-xl p-6 max-w-md w-full"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-white">
                        {mode === 'edit' ? 'Editar Servicio' : 'Nuevo Servicio'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white p-2 hover:bg-red-500/20 rounded-full"
                    >
                        <X size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-gray-300 mb-2">Título</label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                            required
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-gray-300 mb-2">Descripción</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows="4"
                            className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                    </div>

                    <div className="mb-6">
                        <label className="block text-gray-300 mb-2">Icono (puede ser cualquier emoji o texto)</label>
                        <input
                            type="text"
                            value={formData.icon}
                            onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                            placeholder="Ej: 🖌️, 🎨, 📄, o cualquier emoji"
                            className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                            required
                        />
                        <p className="text-gray-400 text-xs mt-1">Puedes usar cualquier emoji, texto o vacío, <a className="text-white" target="_blank" href='https://emojikeyboard.top/es/'>busca emojis aquí</a></p>
                    </div>

                    <div className="flex gap-3">
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex-1 bg-primary hover:bg-primary/80 text-white py-3 rounded-lg smooth-transition font-semibold disabled:opacity-50"
                        >
                            {saving ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mx-auto"></div>
                            ) : (
                                mode === 'edit' ? 'Actualizar' : 'Crear Servicio'
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-lg smooth-transition font-semibold"
                        >
                            Cancelar
                        </button>
                    </div>
                </form>
            </motion.div>
        </motion.div>
    );
};

const GalleryManager = () => {
    const [selectedService, setSelectedService] = useState(null);
    const [servicesData, setServicesData] = useState([]);
    const [serviceImages, setServiceImages] = useState({});
    const [dragOver, setDragOver] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [serverStatus, setServerStatus] = useState('checking');
    const [error, setError] = useState(null);
    const [showServiceModal, setShowServiceModal] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const [serviceModalMode, setServiceModalMode] = useState('create');

    // Check server status and load services
    useEffect(() => {
        checkServerStatus();
        loadServices();
    }, []);

    const checkServerStatus = async () => {
        try {
            const response = await fetch(`${API_BASE}/health`);
            if (response.ok) {
                setServerStatus('connected');
                setError(null);
            } else {
                setServerStatus('disconnected');
            }
        } catch (error) {
            setServerStatus('disconnected');
            setError('No se puede conectar al servidor de archivos');
            console.error('Server not running:', error);
        }
    };

    const loadServices = async () => {
        try {
            const services = await fetchServices();
            setServicesData(services);
            if (services.length > 0 && !selectedService) {
                setSelectedService(services[0]);
            }
            await loadAllServiceImages(services);
        } catch (error) {
            console.error('Error loading services:', error);
            // Fallback to imported data if API fails
            setServicesData(fallbackData);
            if (fallbackData.length > 0) {
                setSelectedService(fallbackData[0]);
            }
            await loadAllServiceImages(fallbackData);
        }
    };

    const loadAllServiceImages = async (services = servicesData) => {
        const loadedImages = {};

        for (const service of services) {
            try {
                const response = await fetch(`${API_BASE}/images/${service.galleryFolder}`);
                if (response.ok) {
                    const images = await response.json();
                    loadedImages[service.galleryFolder] = images;
                } else {
                    loadedImages[service.galleryFolder] = [];
                }
            } catch (error) {
                console.error(`Error loading images for ${service.galleryFolder}:`, error);
                loadedImages[service.galleryFolder] = [];
            }
        }

        setServiceImages(loadedImages);
    };

    const loadServiceImages = async (serviceFolder) => {
        try {
            const response = await fetch(`${API_BASE}/images/${serviceFolder}`);
            if (response.ok) {
                const images = await response.json();
                setServiceImages(prev => ({
                    ...prev,
                    [serviceFolder]: images
                }));
                return images;
            }
        } catch (error) {
            console.error('Error loading service images:', error);
        }
        return [];
    };

    const getServiceImages = (service) => {
        return serviceImages[service?.galleryFolder] || [];
    };

    const handleFileUpload = async (event, serviceId) => {
        const files = Array.from(event.target.files);
        await uploadFiles(files, serviceId);
    };

    const handleDrop = async (event, serviceId) => {
        event.preventDefault();
        setDragOver(false);
        const files = Array.from(event.dataTransfer.files);
        await uploadFiles(files, serviceId);
    };

    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    };

    const uploadFiles = async (files, serviceId) => {
        const service = servicesData.find(s => s.id === serviceId);
        if (!service) return;

        const validFiles = files.filter(file =>
            file.type.startsWith('image/') &&
            ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'].includes(file.type.toLowerCase())
        );

        if (validFiles.length === 0) {
            alert('Por favor, sube solo archivos de imagen (JPEG, PNG, WebP)');
            return;
        }

        setIsProcessing(true);

        try {
            const imagePromises = validFiles.map(file => fileToBase64(file));
            const imageDataArray = await Promise.all(imagePromises);

            const response = await fetch(`${API_BASE}/upload/${service.galleryFolder}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    images: imageDataArray
                }),
            });

            if (response.ok) {
                const result = await response.json();
                await loadServiceImages(service.galleryFolder);
                alert(`✅ ${result.message}`);
            } else {
                throw new Error('Upload failed');
            }
        } catch (error) {
            console.error('Error uploading files:', error);
            alert('❌ Error al subir las imágenes. Asegúrate de que el servidor de archivos esté ejecutándose.');
        } finally {
            setIsProcessing(false);
        }

        if (event && event.target) {
            event.target.value = '';
        }
    };

    const deleteImage = async (serviceFolder, fileName) => {
        if (!window.confirm(`¿Estás seguro de que quieres eliminar ${fileName}?`)) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE}/delete/${serviceFolder}/${fileName}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                await loadServiceImages(serviceFolder);
                alert('✅ Imagen eliminada y secuencia actualizada');
            } else {
                throw new Error('Delete failed');
            }
        } catch (error) {
            console.error('Error deleting image:', error);
            alert('❌ Error al eliminar la imagen');
        }
    };

    const deleteAllImages = async (serviceFolder) => {
        if (!window.confirm('¿Estás seguro de que quieres eliminar TODAS las imágenes de este servicio?')) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE}/delete-all/${serviceFolder}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                await loadServiceImages(serviceFolder);
                alert('✅ Todas las imágenes eliminadas');
            } else {
                throw new Error('Delete all failed');
            }
        } catch (error) {
            console.error('Error deleting all images:', error);
            alert('❌ Error al eliminar las imágenes');
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getTotalImagesCount = () => {
        return Object.values(serviceImages).reduce((total, images) => total + images.length, 0);
    };

    // Service management functions
    const handleCreateService = () => {
        setEditingService(null);
        setServiceModalMode('create');
        setShowServiceModal(true);
    };

    const handleEditService = (service) => {
        setEditingService(service);
        setServiceModalMode('edit');
        setShowServiceModal(true);
    };

    const handleDeleteService = async (service) => {
        if (!window.confirm(`¿Estás seguro de que quieres eliminar el servicio "${service.title}"? Todas sus imágenes también serán eliminadas.`)) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE}/services/${service.id}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                // Refresh services
                await loadServices();
                if (servicesData.length > 0) {
                    setSelectedService(servicesData[0]);
                } else {
                    setSelectedService(null);
                }
                alert('✅ Servicio eliminado correctamente');
            } else {
                const error = await response.json();
                alert(`Error: ${error.error || 'Error al eliminar el servicio'}`);
            }
        } catch (error) {
            console.error('Error deleting service:', error);
            alert('❌ Error al eliminar el servicio');
        }
    };

    const handleServiceSaved = (savedService) => {
        // Refresh services
        loadServices();
        setSelectedService(savedService);
    };

    if (serverStatus === 'checking') {
        return (
            <div className="min-h-screen bg-gray-900 text-white p-4 md:p-6 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-400">Verificando servidor...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-white p-4 md:p-6">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-8"
                >
                    <h1 className="text-3xl md:text-4xl font-bold gradient-text mb-4">
                        Gestor de Galerías
                    </h1>
                    <p className="text-gray-300">
                        Administra las imágenes de los servicios | Total: {getTotalImagesCount()} imágenes guardadas en disco
                    </p>

                    {/* Server Status */}
                    <div className={`mt-4 inline-block px-4 py-2 rounded-full ${serverStatus === 'connected'
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-red-500/20 text-red-400'
                        }`}>
                        {serverStatus === 'connected'
                            ? '✅ Servidor de archivos conectado'
                            : '❌ Servidor desconectado'}
                    </div>

                    {error && (
                        <div className="mt-2 text-red-400 text-sm flex items-center justify-center gap-2">
                            <AlertCircle size={16} />
                            ⚠️ {error}
                        </div>
                    )}
                </motion.div>

                {/* Controls */}
                <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
                    <div className="flex gap-2">
                        <button
                            onClick={handleCreateService}
                            className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg smooth-transition"
                            title="Agregar nuevo servicio"
                        >
                            <Plus size={18} />
                            <span>Nuevo Servicio</span>
                        </button>
                    </div>
                    <div className="flex gap-4">
                        <button
                            onClick={() => window.history.back()}
                            className={`flex items-center gap-2 px-4 py-2 bg-secondary hover:bg-secondary/30 text-white rounded-lg smooth-transition`}
                            title="Regresar"
                        >
                            <StepBack size={18} />
                            <span>Regresar</span>
                        </button>
                        <LogoutButton />
                    </div>
                </div>

                {/* Services Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 mb-8">
                    {servicesData.map((service) => (
                        <motion.div
                            key={service.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            whileHover={{ scale: 1.02, y: -2 }}
                            className={`bg-gray-800 rounded-xl p-4 sm:p-6 cursor-pointer smooth-transition border-2 relative group ${selectedService?.id === service.id
                                ? 'border-primary bg-primary/10'
                                : 'border-gray-700 hover:border-primary/50'
                                }`}
                            onClick={() => setSelectedService(service)}
                        >
                            {/* Add edit/delete buttons - visible on hover */}
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 z-10">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditService(service);
                                    }}
                                    className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg smooth-transition"
                                    title="Editar servicio"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                        <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z"></path>
                                    </svg>
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteService(service);
                                    }}
                                    className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg smooth-transition"
                                    title="Eliminar servicio"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>

                            <div className="text-center">
                                <div className="text-3xl sm:text-4xl mb-3">{service.icon}</div>
                                <h3 className="text-base sm:text-lg font-bold text-white mb-2 line-clamp-2 leading-tight">
                                    {service.title}
                                </h3>
                                <div className="flex items-center justify-center space-x-2 text-xs sm:text-sm text-gray-300">
                                    <Image size={14} />
                                    <span>{getServiceImages(service).length} imágenes</span>
                                </div>
                                {serverStatus === 'connected' && (
                                    <div className="mt-2 text-xs text-green-400 bg-green-500/10 rounded-full px-2 py-1">
                                        📁 {service.galleryFolder}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Service Detail View */}
                <AnimatePresence>
                    {selectedService && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 20 }}
                            className="bg-gray-800 rounded-xl p-4 sm:p-6 mb-8 border border-gray-700"
                        >
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center space-x-3 mb-2">
                                        <span className="text-2xl sm:text-3xl">{selectedService.icon}</span>
                                        <div>
                                            <h2 className="text-xl sm:text-2xl font-bold text-white">
                                                {selectedService.title}
                                            </h2>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setSelectedService(null)}
                                    className="text-gray-400 hover:text-white smooth-transition p-2 hover:bg-red-500/20 rounded-full self-start sm:self-auto"
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            {/* Upload Section */}
                            <div className="mb-8">
                                <div className="flex items-center space-x-3 mb-4">
                                    <div className="w-2 h-8 bg-primary rounded-full"></div>
                                    <h3 className="text-xl font-bold text-white">
                                        Subir y Guardar en Disco
                                    </h3>
                                </div>

                                {serverStatus === 'disconnected' ? (
                                    <div className="border-2 border-dashed border-red-500/50 rounded-xl p-8 text-center bg-red-500/10">
                                        <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
                                        <p className="text-red-400 text-lg mb-2">
                                            Servidor de archivos no disponible
                                        </p>
                                        <p className="text-sm text-red-400/80">
                                            Ejecuta: <code className="bg-black/30 px-2 py-1 rounded">node file-server.js</code>
                                        </p>
                                    </div>
                                ) : (
                                    <div
                                        className={`border-2 border-dashed rounded-xl p-8 text-center smooth-transition ${dragOver
                                            ? 'border-primary bg-primary/10'
                                            : 'border-gray-600 hover:border-primary/50 bg-gray-700/30'
                                            }`}
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            setDragOver(true);
                                        }}
                                        onDragLeave={() => setDragOver(false)}
                                        onDrop={(e) => handleDrop(e, selectedService.id)}
                                    >
                                        <Upload size={48} className="mx-auto text-gray-400 mb-4" />
                                        <p className="text-lg text-gray-300 mb-4">
                                            Arrastra imágenes aquí para guardar en disco
                                        </p>

                                        <input
                                            type="file"
                                            multiple
                                            accept="image/jpeg,image/png,image/webp,image/jpg"
                                            onChange={(e) => handleFileUpload(e, selectedService.id)}
                                            className="hidden"
                                            id={`file-upload-${selectedService.id}`}
                                        />
                                        <label
                                            htmlFor={`file-upload-${selectedService.id}`}
                                            className="bg-primary hover:bg-primary/80 text-white px-8 py-4 rounded-lg cursor-pointer smooth-transition inline-flex items-center space-x-3 text-lg font-semibold"
                                        >
                                            {isProcessing ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                                                    <span>Guardando en disco...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Plus size={24} />
                                                    <span>Seleccionar y Guardar Imágenes</span>
                                                </>
                                            )}
                                        </label>

                                        <p className="text-sm text-gray-400 mt-4">
                                            Los archivos se guardarán automáticamente como: img_timestamp_random.jpg
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Images Grid */}
                            {getServiceImages(selectedService).length > 0 ? (
                                <div>
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
                                        <div>
                                            <h3 className="text-lg sm:text-xl font-bold text-white">
                                                Imágenes en Disco ({getServiceImages(selectedService).length})
                                            </h3>
                                            <p className="text-gray-400 text-sm">
                                                ✅ Estas imágenes están guardadas físicamente en el servidor
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => deleteAllImages(selectedService.galleryFolder)}
                                            className="text-red-400 hover:text-red-300 smooth-transition flex items-center space-x-2 bg-red-500/10 hover:bg-red-500/20 px-4 py-2 rounded-lg text-sm"
                                        >
                                            <Trash2 size={16} />
                                            <span>Eliminar Todas</span>
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 sm:gap-4">
                                        {getServiceImages(selectedService).map((image) => (
                                            <motion.div
                                                key={image.name}
                                                initial={{ opacity: 0, scale: 0.8 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                whileHover={{ scale: 1.05 }}
                                                className="relative group aspect-square rounded-lg overflow-hidden bg-gray-800 border border-gray-700"
                                            >
                                                <img
                                                    src={`${FILE_SERVER_URL}${image.path}`}
                                                    alt={`${selectedService.title} ${image.number}`}
                                                    className="w-full h-full object-cover cursor-pointer"
                                                    onClick={() => setPreviewImage({ ...image, service: selectedService })}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = `${FILE_SERVER_URL}/images/gallery/default.jpg`;
                                                    }}
                                                />

                                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/70 smooth-transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                                                    <div className="flex space-x-2">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                window.open(`${FILE_SERVER_URL}${image.path}`, '_blank');
                                                            }}
                                                            className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg smooth-transition transform hover:scale-110"
                                                            title="Abrir original"
                                                        >
                                                            <Download size={14} />
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                deleteImage(selectedService.galleryFolder, image.name);
                                                            }}
                                                            className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg smooth-transition transform hover:scale-110"
                                                            title="Eliminar del disco"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="absolute top-2 left-2 bg-black/80 text-white text-xs px-2 py-1 rounded font-mono">
                                                    #{image.name.split('_')[1] || '?'}
                                                </div>

                                                <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                                                    DISCO
                                                </div>

                                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 text-xs text-white">
                                                    <div className="truncate font-mono">{image.name}</div>
                                                    <div className="text-gray-300 text-xs">{formatFileSize(image.size)}</div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <FolderOpen size={48} className="mx-auto text-gray-600 mb-4" />
                                    <h3 className="text-xl font-bold mb-2">No hay imágenes guardadas</h3>
                                    <p className="text-gray-400 mb-2">
                                        {serverStatus === 'connected'
                                            ? 'Sube imágenes usando el botón de arriba'
                                            : 'Conecta el servidor para ver las imágenes'
                                        }
                                    </p>
                                </div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Image Preview Modal */}
                <AnimatePresence>
                    {previewImage && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
                            onClick={() => setPreviewImage(null)}
                        >
                            <div className="relative max-w-4xl w-full max-h-full">
                                <button
                                    className="absolute top-4 right-4 z-10 text-white p-3 rounded-lg bg-gray-800 hover:bg-red-600 smooth-transition"
                                    onClick={() => setPreviewImage(null)}
                                >
                                    <X size={24} />
                                </button>

                                <div className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700">
                                    <div className="p-4 border-b border-gray-700">
                                        <h3 className="text-white font-semibold text-lg">
                                            {previewImage.service.title} - Imagen
                                        </h3>
                                        <p className="text-gray-300 text-sm">
                                            Archivo: {previewImage.name} • {formatFileSize(previewImage.size)}
                                        </p>
                                    </div>

                                    <div className="p-4 sm:p-6 flex items-center justify-center bg-gray-900">
                                        <img
                                            src={`${FILE_SERVER_URL}${previewImage.path}`}
                                            alt={`${previewImage.service.title}`}
                                            className="w-full h-auto max-h-[60vh] object-contain rounded-lg"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = `${FILE_SERVER_URL}/images/gallery/default.jpg`;
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Service Form Modal */}
                <ServiceFormModal
                    isOpen={showServiceModal}
                    onClose={() => setShowServiceModal(false)}
                    onSave={handleServiceSaved}
                    service={editingService}
                    mode={serviceModalMode}
                />
            </div>
        </div>
    );
};

export default GalleryManager;